import {CheckMode} from '../../ruleManagement/models/rule-management.models';
import {normalizeFileTypeToken} from '../../../models/ruleset-file-match';
import {Manifest} from '../../../api/model/manifest';
import {StepNode} from '../../../api/model/stepNode';

export type TriggerMode = 'manual' | 'fileUpload' | 'schedule' | 'webhook';
export type SelectionMode = 'manual' | 'predefined';
export type RulesetType = 'IDS' | 'HYBRID' | 'KI';
export type PredefinedFileSelectionScope = 'all' | 'selected';

/**
 * Alle wählbaren Dateitypen (entsprechen den API-Enum-Werten von ProjectFile.FileTypeEnum,
 * ohne FOLDER, da Ordner keine prüfbaren Dateien sind).
 */
export const ALL_FILE_TYPES: string[] = ['IFC', 'PDF', 'EXCEL', 'WORD', 'POWERPOINT', 'COORDINATION', 'UNKNOWN'];

/** Reihenfolge der Ergebnisebenen im Bericht (Berichtsperspektive) */
export type ReportPerspective = 'rules_first' | 'files_first' | 'structure_first';

/** Granularität der automatischen Aufgabenerstellung */
export type TaskCreationLevel = 'per_check' | 'per_rule' | 'per_file' | 'per_detail';

/** Eine einzelne Spalte (Anzeigewert) innerhalb einer Berichtsebene */
export interface ReportColumnConfig {
  /** Stabiler Schlüssel der Spalte (z. B. 'status') */
  key: string;
  /** Anzeigename der Spalte (z. B. 'Status') */
  label: string;
  /** Sichtbarkeit der Spalte im Bericht (Auge-Toggle) */
  visible: boolean;
  /**
   * Ob sich der Wert dieser Spalte pro Prüfschritt unterscheiden kann (KI-Generiert)
   * oder für alle Prüfschritte derselben Regel konstant ist (Prüfparameter/Eigener Wert).
   * Undefined wird wie `true` behandelt (Altkonfigurationen ohne dieses Feld).
   */
  perStep?: boolean;
}

/** Eine Berichtsebene (z. B. "Prüfregeln", "Dateien", "Detailergebnis") */
export interface ReportLevelConfig {
  /** Stabiler Schlüssel der Ebene ('rules' | 'files' | 'structure' | 'detail') */
  key: string;
  /** Anzeigename der Ebene */
  label: string;
  /** Spalten der Ebene in gewünschter Reihenfolge */
  columns: ReportColumnConfig[];
}

/** Berichtskonfiguration je Prüfregelset */
export interface RulesetReportConfig {
  rulesetId: string;
  rulesetType: RulesetType;
  perspective: ReportPerspective;
  /** Ebenen in der durch die Perspektive bestimmten Reihenfolge */
  levels: ReportLevelConfig[];
  /**
   * ID des Set-Eingabeparameters, dessen Wert in der Strukturebene als „Identifikator"-Spalte
   * angezeigt wird. Default = der im Set konfigurierte identifikatorParamId. Der Renderer nutzt
   * den zugehörigen Positions-Wert (structure.name).
   */
  structureIdentifikatorParamId?: string;
}

export interface MyCheckBuilderState {
  // Basics
  name: string;
  description: string;
  currentStep: number;
  expertMode: boolean;

  // Start
  triggerMode: TriggerMode;

  // Regelauswahl
  ruleSelectionMode: SelectionMode;
  selectedRuleSetIds: string[];
  selectedRuleIds: string[];
  /**
   * Sets, aus denen nur einzelne Regeln (selectedRuleIds) gewählt sind. Die Zuordnung
   * Regel→Set ist aus den IDs allein nicht ableitbar; ohne dieses Feld ginge der
   * Teilauswahl-Zustand (intermediate Checkbox, Berichts-Tab pro Set) beim Navigieren
   * bzw. erneuten Laden verloren.
   */
  partiallySelectedRuleSetIds: string[];
  /**
   * Typ je ausgewähltem Regelset (`rulesetId` → Typ).
   *
   * Notwendig, weil `selectedRulesetType` nur EINEN Typ trägt und eine gemischte Auswahl damit
   * nicht darstellbar ist. Daraus wird entschieden, ob die typspezifische Kette oder der
   * Dispatcher (`multi_check_executor`) ins Manifest geschrieben wird – ohne den Builder von
   * `RulesetService` abhängig zu machen (dieses Modul ist bewusst dependency-frei).
   */
  selectedRulesetTypes: Record<string, RulesetType>;
  /**
   * Bleibt für den Ein-Regelset-Fall und Alt-Manifeste erhalten. Bei mehreren Regelsets ist der
   * Wert nicht aussagekräftig – dann gilt {@link selectedRulesetTypes}.
   */
  selectedRulesetType: RulesetType;
  showRuleFiltersAtExecution: boolean;

  // AI-Prompts (Teil der Definition; vorbelegt aus dem gewählten AI-Regelset, im Frontend editierbar)
  aiSystemPrompt?: string;
  aiTemplatePrompt?: string;
  aiModel?: string;
  // AI-Parametermodell (set-level; vorbelegt aus dem gewählten AI-Regelset)
  aiInputParameters?: any[];
  aiCheckParameters?: any[];
  aiOutputParameters?: any[];
  aiTags?: any[];
  // Vom AI-Regelset vererbte Kontextdateien (read-only im Builder; Hintergrundwissen)
  aiContextFiles?: any[];

  // Dateiauswahl
  fileSelectionMode: SelectionMode;
  predefinedFileSelectionScope: PredefinedFileSelectionScope;
  predefinedUploadPeriod: string;
  predefinedCreatorIds: number[];
  predefinedFileNameRegex: string;
  fileTypes: string[];
  documentClasses: string[];
  minFiles: number;
  /** Fehlt/undefined = unbegrenzt (siehe updateMaxFiles im builder-file-step). */
  maxFiles?: number;
  userHint: string;

  // Bericht
  inAppReport: boolean;
  excelExport: boolean;
  pdfExport: boolean;
  bcfExport: boolean;
  createTask: boolean;
  taskCreationLevel: TaskCreationLevel;
  /** Präfix für den Namen automatisch erstellter Aufgaben */
  taskNamePrefix: string;
  storeInProjectFolder: boolean;
  /** Zielordner für die Berichtsablage */
  reportTargetFolder: string;
  /** Dateiname-Muster (mit Platzhaltern wie {Projekt}, {Datum}) */
  reportFileNamePattern: string;
  emailNotification: boolean;
  /** IDs der zu benachrichtigenden Projektmitglieder */
  notificationMemberIds: number[];
  /** Externe E-Mail-Adressen für die Benachrichtigung */
  recipients: string[];

  // Berichtsstruktur (pro Prüfregelset)
  reportConfigs: RulesetReportConfig[];
}

export function createDefaultBuilderState(): MyCheckBuilderState {
  return {
    name: '',
    description: '',
    currentStep: 0,
    expertMode: false,
    triggerMode: 'manual',
    ruleSelectionMode: 'manual',
    selectedRuleSetIds: [],
    selectedRuleIds: [],
    partiallySelectedRuleSetIds: [],
    selectedRulesetTypes: {},
    selectedRulesetType: 'IDS',
    showRuleFiltersAtExecution: false,
    aiSystemPrompt: '',
    aiTemplatePrompt: '',
    aiModel: '',
    aiInputParameters: [],
    aiCheckParameters: [],
    aiOutputParameters: [],
    aiTags: [],
    aiContextFiles: [],
    fileSelectionMode: 'manual',
    predefinedFileSelectionScope: 'all',
    predefinedUploadPeriod: 'all_time',
    predefinedCreatorIds: [],
    predefinedFileNameRegex: '',
    fileTypes: ALL_FILE_TYPES,
    documentClasses: [],
    minFiles: 1,
    maxFiles: 10,
    userHint: '',
    inAppReport: true,
    excelExport: false,
    pdfExport: false,
    bcfExport: false,
    createTask: false,
    taskCreationLevel: 'per_check',
    taskNamePrefix: '',
    storeInProjectFolder: false,
    reportTargetFolder: '',
    reportFileNamePattern: '',
    emailNotification: false,
    notificationMemberIds: [],
    recipients: [],
    reportConfigs: [],
  };
}

const LEGACY_IDS_REQUIREMENTS_LEVEL_LABEL = 'Facetten';
const IDS_REQUIREMENTS_LEVEL_LABEL = 'Anforderungen';

/**
 * Migriert ältere IDS-Berichtskonfigurationen, in denen die 3. Ebene noch
 * "Facetten" heißt, auf die neue Bezeichnung "Anforderungen".
 */
export function normalizeIdsReportConfigLabels(configs: RulesetReportConfig[]): RulesetReportConfig[] {
  let changed = false;
  const normalized = configs.map(config => {
    let configChanged = false;
    const levels = config.levels.map(level => {
      const isRequirementsLevel = level.key === 'requirements' || level.key === 'detail';
      const hasLegacyLabel = level.label === LEGACY_IDS_REQUIREMENTS_LEVEL_LABEL;
      if (!isRequirementsLevel || !hasLegacyLabel) return level;
      configChanged = true;
      return {...level, label: IDS_REQUIREMENTS_LEVEL_LABEL};
    });

    if (!configChanged) return config;
    changed = true;
    return {...config, levels};
  });

  return changed ? normalized : configs;
}

// ─── Berichtsstruktur: Optionen & Default-Konfiguration ──────────────────────

export const REPORT_PERSPECTIVE_OPTIONS: { value: ReportPerspective; label: string }[] = [
  {value: 'rules_first', label: 'Zuerst Prüfregeln, dann Dateien (geeignet für fachliche Prüfung)'},
  {value: 'files_first', label: 'Zuerst Dateien, dann Prüfregeln (geeignet für dokumentenbezogene Prüfung)'},
  {
    value: 'structure_first',
    label: 'Zuerst Dateien und Dateistrukturen, dann Prüfregeln (geeignet für strukturbezogene Regelprüfung)',
  },
];

// ─── Ebenen-Reihenfolgen abhängig von der Prüfebene (3 vs. 4 Spalten) ────────

/** Anzeige-Namen der Berichtsebenen. */
export const REPORT_LEVEL_LABELS: Record<string, string> = {
  files: 'Dateien',
  structure: 'Strukturebene',
  rules: 'Prüfregeln',
  detail: 'Ergebnis',
};

/**
 * Grauer Klammer-Hinweis je Berichtsperspektive, keyed by der Nicht-Detail-Reihenfolge
 * (z. B. 'files>structure>rules'). Der eigentliche Label ist die Pfeil-Darstellung der Ebenen;
 * dieser Hinweis wird im Dropdown in Grau in Klammern dahinter angezeigt.
 */
export const REPORT_ORDERING_HINTS: Record<string, string> = {
  // 3 Spalten (Gesamtdokument)
  'rules>files': 'geeignet für fachliche Prüfung',
  'files>rules': 'geeignet für dokumentenbezogene Prüfung',
  // 4 Spalten (mit Strukturebene) – „files immer vor structure"
  'files>structure>rules': 'geeignet für komplexe Dateien mit vielen Strukturebenen',
  'files>rules>structure': 'geeignet für Prüfung je Datei, nach Regel gruppiert',
  'rules>files>structure': 'geeignet für regelweise Auswertung über alle Dateien',
  // Gemeinsamer Prüflauf (COMBINED): ohne Dateiebene, weil es kein Ergebnis je Datei gibt.
  'rules': 'alle Dateien werden gemeinsam geprüft',
  'rules>structure': 'alle Dateien werden gemeinsam geprüft',
};

/** Ob die Prüfebene eine Strukturebene (Positionen) erzeugt → 4 statt 3 Spalten. */
export function hasStructureLevel(pruefebene: string | undefined | null): boolean {
  const normalized = String(pruefebene ?? '').toLowerCase();
  return normalized !== '' && normalized !== 'gesamtdokument';
}

/** Eine gültige Ebenen-Reihenfolge für das Berichts-Dropdown (detail ist immer die letzte, fixe Ebene). */
export interface LevelOrdering {
  /** Nicht-Detail-Ebenen in Reihenfolge. */
  keys: string[];
  /** Stabiler Wert inkl. detail (z. B. 'files>structure>rules>detail'). */
  value: string;
  /** Pfeil-Anzeigename, z. B. „Dateien → Strukturebene → Prüfregeln → Ergebnis". */
  label: string;
  /** Grauer Klammer-Hinweis (ohne Klammern), z. B. „geeignet für fachliche Prüfung". */
  hint: string;
  /**
   * Fertiger grauer Hinweis-Suffix inkl. genau einem führenden (geschützten) Leerzeichen und
   * Klammern, z. B. „ (geeignet für fachliche Prüfung)"; leer, wenn kein Hinweis. Wird im
   * Dropdown direkt an den Label gehängt – so ist der Abstand deterministisch (kein Template-
   * Whitespace nötig, konsistent in Liste und zugeklapptem Feld).
   */
  hintSuffix: string;
}

function levelPermutations<T>(items: T[]): T[][] {
  if (items.length <= 1) return [items];
  const result: T[][] = [];
  items.forEach((item, i) => {
    const rest = [...items.slice(0, i), ...items.slice(i + 1)];
    for (const perm of levelPermutations(rest)) result.push([item, ...perm]);
  });
  return result;
}

/**
 * Gültige Ebenen-Reihenfolgen für die gegebene Prüfebene.
 * - Gesamtdokument → 3 Ebenen (files, rules) + detail: files→rules bzw. rules→files.
 * - sonst → 4 Ebenen (files, structure, rules) + detail; Zwang: **files immer vor structure**.
 * Die detail-Ebene ist immer die letzte (fixe) Ebene und nicht Teil der Auswahl.
 *
 * Im Prüfmodus `COMBINED` entfällt die **Dateiebene komplett**: der gemeinsame Prüflauf liefert ein
 * Ergebnis je Prüfregel über die gesamte Dateimenge, kein Ergebnis je Datei. Eine Dateispalte wäre
 * dort nur eine künstliche Aufspaltung derselben Bewertung über ihre Quelldateien. Der Dateibezug
 * bleibt im Begründungstext der Antwort.
 */
export function validLevelOrderings(
  pruefebene: string | undefined | null,
  checkMode?: CheckMode,
): LevelOrdering[] {
  const withStructure = hasStructureLevel(pruefebene);
  const nonDetail = checkMode === 'COMBINED'
    ? (withStructure ? ['rules', 'structure'] : ['rules'])
    : (withStructure ? ['files', 'structure', 'rules'] : ['files', 'rules']);
  return levelPermutations(nonDetail)
    .filter(order => {
      const fi = order.indexOf('files');
      const si = order.indexOf('structure');
      if (si === -1) return true;
      // Mit Dateiebene: files immer vor structure. Ohne Dateiebene (COMBINED) bleibt davon
      // „die Strukturebene steht nie an erster Stelle" – sie gruppiert innerhalb der Prüfregel.
      return fi >= 0 ? fi < si : si > 0;
    })
    .map(keys => {
      const full = [...keys, 'detail'];
      const hint = REPORT_ORDERING_HINTS[keys.join('>')] ?? '';
      return {
        keys,
        value: full.join('>'),
        label: full.map(key => REPORT_LEVEL_LABELS[key] ?? key).join(' → '),
        hint,
        // Geschütztes Leerzeichen ( ) als Trenner → genau ein Space, unabhängig von
        // Angular-Template-Whitespace (der sonst je nach Kontext 0 oder 2 Leerzeichen ergibt).
        hintSuffix: hint ? ` (${hint})` : '',
      };
    });
}

/** Stabiler Reihenfolge-Wert einer Levels-Konfiguration (für die Dropdown-Vorauswahl). */
export function orderingValueFromLevels(levels: ReportLevelConfig[] | undefined | null): string {
  const keys = (levels ?? []).map(level => level.key).filter(key => !!key);
  const nonDetail = keys.filter(key => key !== 'detail');
  return [...nonDetail, 'detail'].join('>');
}

/**
 * Baut ReportLevelConfig[] in gegebener Reihenfolge (+ detail als Letztes). Spalten/Überschriften
 * bestehender Ebenen bleiben erhalten; fehlende Ebenen bekommen die Default-Spalten.
 */
export function createReportLevelsFromOrder(
  orderKeys: string[],
  existingLevels?: ReportLevelConfig[],
  detailColumns?: ReportColumnConfig[],
): ReportLevelConfig[] {
  const byKey = new Map((existingLevels ?? []).map(level => [level.key, level]));
  const keys = [...orderKeys.filter(key => key !== 'detail'), 'detail'];
  return keys.map(key => {
    const existing = byKey.get(key);
    const columns = key === 'detail' && detailColumns && detailColumns.length > 0
      ? detailColumns.map(col => ({...col}))
      : (existing?.columns?.map(col => ({...col})) ?? (DEFAULT_LEVEL_COLUMNS[key] ?? []).map(col => ({...col})));
    return {
      key,
      label: existing?.label ?? REPORT_LEVEL_LABELS[key] ?? key,
      columns,
    };
  });
}

/**
 * Migriert die Ebenen einer Berichtskonfiguration auf eine Reihenfolge, die zu Prüfebene UND
 * Prüfmodus des Regelsets passt; Spalten und Überschriften bestehender Ebenen bleiben erhalten.
 *
 * Nötig, weil die gespeicherte Reihenfolge den Stand des Regelsets zum Zeitpunkt des Speicherns
 * abbildet: wird die Prüfebene später geändert, passt die Spaltenzahl nicht mehr; wird der
 * Prüfmodus auf „Alle Dateien zusammen prüfen" gestellt, enthält sie eine Dateiebene, die es im
 * gemeinsamen Lauf nicht mehr gibt. Beide Richtungen deckt derselbe Abgleich gegen
 * {@link validLevelOrderings} ab – ein Rückwechsel auf SINGLE holt die Dateiebene wieder zurück.
 * Gibt bewusst DIESELBE Instanz zurück, wenn nichts zu migrieren ist – der Aufrufer erkennt daran,
 * ob er den State (und damit das Manifest) überhaupt anfassen muss.
 */
export function migrateLevelsToRuleset(
  config: RulesetReportConfig,
  pruefebene: string | undefined | null,
  checkMode?: CheckMode,
  detailColumns?: ReportColumnConfig[],
): RulesetReportConfig {
  const orderings = validLevelOrderings(pruefebene, checkMode);
  const storedOrdering = orderingValueFromLevels(config.levels);
  if (orderings.some(ordering => ordering.value === storedOrdering)) return config;
  return {
    ...config,
    levels: createReportLevelsFromOrder(orderings[0].keys, config.levels, detailColumns),
  };
}

export const TASK_CREATION_LEVEL_OPTIONS: { value: TaskCreationLevel; label: string }[] = [
  {value: 'per_check', label: 'Aufgabe pro Prüfung'},
  {value: 'per_rule', label: 'Aufgabe pro Prüfregel'},
  {value: 'per_file', label: 'Aufgabe pro Datei'},
  {value: 'per_detail', label: 'Aufgabe pro Detailergebnis'},
];

/**
 * Standard-Spalten je Ebenen-Typ. Diese Liste bildet die Fallback-/Default-Konfiguration
 * für Berichte, die noch keine gespeicherte Konfiguration besitzen (z. B. Altberichte).
 */
/** Stabiler Schlüssel der identifizierenden Struktur-Spalte (Label = gewählter Set-Parametername). */
export const STRUCTURE_IDENTIFIKATOR_KEY = 'identifikator';

/**
 * Verfügbare Spalten je Berichtsebene (Status immer als erste Position), inkl. Default-Sichtbarkeit
 * (`defaultVisible`, Default `true`). Palette für das Popup „Anzeigewerte bearbeiten"; `detail` wird
 * dynamisch aus den Ausgabeparametern abgeleitet (outputParamsToDetailColumns).
 */
export const AVAILABLE_LEVEL_COLUMNS: Record<string, { key: string; label: string; defaultVisible?: boolean }[]> = {
  files: [
    {key: 'status', label: 'Status'},
    {key: 'fileName', label: 'Dateiname'},
    {key: 'fileFormat', label: 'Dateiformat', defaultVisible: false},
    {key: 'rating', label: 'Ergebnis'},
  ],
  rules: [
    {key: 'status', label: 'Status'},
    {key: 'shortCode', label: 'Kürzel'},
    {key: 'name', label: 'Prüfregelname'},
    {key: 'description', label: 'Beschreibung', defaultVisible: false},
    {key: 'rating', label: 'Ergebnis'},
  ],
  structure: [
    {key: 'status', label: 'Status'},
    {key: STRUCTURE_IDENTIFIKATOR_KEY, label: 'Identifikator'},
    {key: 'rating', label: 'Ergebnis'},
  ],
};

/** Schlüssel der identifizierenden (nie ausblendbaren) 2. Spalte je Ebene. */
export const IDENTIFYING_COLUMN_KEY: Record<string, string> = {
  files: 'fileName',
  rules: 'name',
  structure: STRUCTURE_IDENTIFIKATOR_KEY,
};

/** Ob eine Ebene per Drag umsortierbar ist (detail = Ausgabeparameter-Reihenfolge, fix). */
export function isReportLevelReorderable(levelKey: string): boolean {
  return levelKey !== 'detail';
}

/** Ob die Sichtbarkeit einer Spalte gesperrt ist (Status immer; identifizierende 2. Spalte je Ebene). */
export function isReportColumnVisibilityLocked(levelKey: string, columnKey: string): boolean {
  if (columnKey === 'status') return true;
  return IDENTIFYING_COLUMN_KEY[levelKey] === columnKey;
}

/** Ob eine Spalte per Drag verschiebbar ist (Status ist fix an Position 1; detail gar nicht). */
export function isReportColumnDraggable(levelKey: string, columnKey: string): boolean {
  return isReportLevelReorderable(levelKey) && columnKey !== 'status';
}

/**
 * Gleicht die gespeicherten Spalten einer Ebene mit der aktuellen Palette (AVAILABLE_LEVEL_COLUMNS)
 * ab: bekannte Spalten behalten Reihenfolge + Sichtbarkeit, fehlende werden ergänzt, unbekannte
 * (Alt-Schlüssel) entfernt. Status wird an Position 1 erzwungen; Status und die identifizierende
 * Spalte sind immer sichtbar. Für 'detail' bleibt die (dynamische) Spaltenliste unverändert.
 */
export function reconcileLevelColumns(
  levelKey: string,
  existing: ReportColumnConfig[] | undefined,
  identifikatorLabel?: string,
): ReportColumnConfig[] {
  if (levelKey === 'detail') return (existing ?? []).map(col => ({...col}));
  const available = AVAILABLE_LEVEL_COLUMNS[levelKey] ?? [];
  if (available.length === 0) return (existing ?? []).map(col => ({...col}));
  const existingByKey = new Map((existing ?? []).map(col => [col.key, col]));
  const orderedKeys: string[] = [];
  for (const col of existing ?? []) {
    if (available.some(a => a.key === col.key) && !orderedKeys.includes(col.key)) orderedKeys.push(col.key);
  }
  for (const a of available) if (!orderedKeys.includes(a.key)) orderedKeys.push(a.key);

  const cols = orderedKeys.map(key => {
    const a = available.find(x => x.key === key)!;
    const ex = existingByKey.get(key);
    const label = key === STRUCTURE_IDENTIFIKATOR_KEY ? (identifikatorLabel || a.label) : a.label;
    const locked = isReportColumnVisibilityLocked(levelKey, key);
    return {key, label, visible: locked ? true : (ex ? ex.visible : a.defaultVisible !== false)};
  });
  // Status immer an Position 1.
  return [...cols.filter(c => c.key === 'status'), ...cols.filter(c => c.key !== 'status')];
}

const DEFAULT_LEVEL_COLUMNS: Record<string, ReportColumnConfig[]> = {
  ...Object.fromEntries(
    Object.entries(AVAILABLE_LEVEL_COLUMNS).map(([key, cols]) => [
      key,
      cols.map(c => ({key: c.key, label: c.label, visible: c.defaultVisible !== false})),
    ]),
  ),
  // detail wird i. d. R. dynamisch aus den Ausgabeparametern gebaut; minimaler Fallback.
  detail: [{key: 'status', label: 'Status', visible: true, perStep: true}],
};

/** Reihenfolge & Beschriftung der Ebenen je Berichtsperspektive */
const PERSPECTIVE_LEVEL_ORDER: Record<ReportPerspective, { key: string; label: string }[]> = {
  rules_first: [
    {key: 'rules', label: 'Prüfregeln'},
    {key: 'files', label: 'Dateien'},
    {key: 'detail', label: 'Detailergebnis'},
  ],
  files_first: [
    {key: 'files', label: 'Dateien'},
    {key: 'rules', label: 'Prüfregeln'},
    {key: 'detail', label: 'Detailergebnis'},
  ],
  structure_first: [
    {key: 'structure', label: 'Dateien & Strukturen'},
    {key: 'rules', label: 'Prüfregeln'},
    {key: 'detail', label: 'Detailergebnis'},
  ],
};

/** Erstellt die Default-Ebenen (inkl. Spalten) für eine Berichtsperspektive. */
export function createDefaultReportLevels(
  perspective: ReportPerspective,
  detailColumns?: ReportColumnConfig[],
): ReportLevelConfig[] {
  return PERSPECTIVE_LEVEL_ORDER[perspective].map(level => ({
    key: level.key,
    label: level.label,
    columns: (level.key === 'detail' && detailColumns && detailColumns.length > 0)
      ? detailColumns.map(col => ({...col}))
      : (DEFAULT_LEVEL_COLUMNS[level.key] ?? []).map(col => ({...col})),
  }));
}

/**
 * Leitet die Detail-Spalten vollständig dynamisch aus den benutzerdefinierten
 * KI-Ausgabeparametern ab. Jeder Ausgabeparameter wird zu einer Spalte, deren
 * Schlüssel und Label dem konfigurierten Parameter-Namen entsprechen – so deckt
 * sich die Berichtsstruktur exakt mit dem frei konfigurierbaren Ausgabeformat.
 * Eine führende Status-Spalte bleibt für die visuelle Statusanzeige erhalten.
 */
export function outputParamsToDetailColumns(outputParameters?: any[]): ReportColumnConfig[] {
  const params = Array.isArray(outputParameters) ? outputParameters : [];
  const columns: ReportColumnConfig[] = [{key: 'status', label: 'Status', visible: true, perStep: true}];
  const seen = new Set<string>(['status']);
  for (const param of params) {
    const name = (param?.name ?? '').toString().trim();
    if (!name) continue;
    const dedupeKey = name.toLowerCase();
    if (seen.has(dedupeKey)) continue;
    seen.add(dedupeKey);
    // 'Prüfparameter' und 'Eigener Wert' liefern denselben Wert für jeden Prüfschritt
    // derselben Regel (Regel- bzw. Set-level festgelegt); nur 'KI-Generiert' (Default,
    // falls modus fehlt) variiert tatsächlich pro identifiziertem Prüfschritt.
    const modus = (param?.modus ?? '').toString().trim().toLowerCase();
    const perStep = modus === '' || modus.startsWith('ki') || modus.startsWith('ai');
    columns.push({key: name, label: name, visible: true, perStep});
  }
  return columns;
}

/**
 * Aktualisiert bei bereits gespeicherten KI-Berichtskonfigurationen die Detail-Spalten
 * anhand der aktuellen Ausgabeparameter-Definition. `getConfig()` liefert für ein bereits
 * konfiguriertes Regelset immer die gespeicherte Konfiguration zurück, ohne die Spalten neu
 * abzuleiten. Zwei Fälle:
 *  - Der Spalten-Schlüsselsatz entspricht noch genau den aktuellen Ausgabeparametern:
 *    Sichtbarkeit/Label (nutzerdefiniert) bleiben je Spalte erhalten, aber Reihenfolge und
 *    `perStep` werden aus den aktuellen Ausgabeparametern übernommen – Detail-Spalten sind im
 *    Report-Builder ohnehin nicht manuell verschiebbar, eine abweichende gespeicherte
 *    Reihenfolge ist daher immer nur Altstand aus einer früheren KI-Editor-Reihenfolge.
 *  - Der Spalten-Schlüsselsatz weicht ab (z. B. eine sehr alte Konfiguration aus der Zeit vor
 *    den dynamischen Ausgabeparametern, mit den festen Spalten status/rating/result/
 *    recommendation/source): die komplette Spaltenliste wird aus den aktuellen
 *    Ausgabeparametern neu abgeleitet, da die alten Schlüssel ohnehin ins Leere laufen
 *    (`resolveDetailColumnValue` findet dafür keine Werte in `fields`).
 */
export function refreshKiDetailColumns(
  configs: RulesetReportConfig[],
  outputParameters?: any[],
): RulesetReportConfig[] {
  const freshColumns = outputParamsToDetailColumns(outputParameters);
  // Ohne geladene Ausgabeparameter enthält freshColumns nur die Status-Spalte. Dann darf keine
  // bestehende (evtl. korrekt konfigurierte) Spaltenliste ersetzt werden – sonst würde beim
  // Öffnen des Builders vor dem Laden der Parameter alles auf [status] reduziert.
  if (freshColumns.length <= 1) return configs;
  const freshKeySet = new Set(freshColumns.map(c => c.key.toLowerCase()));
  const freshByKey = new Map(freshColumns.map(c => [c.key.toLowerCase(), c]));

  let changed = false;
  const normalized = configs.map(config => {
    if (config.rulesetType !== 'KI') return config;
    let levelChanged = false;
    const levels = config.levels.map(level => {
      if (level.key !== 'detail') return level;

      const storedKeySet = new Set(level.columns.map(c => c.key.toLowerCase()));
      const keysMatch = storedKeySet.size === freshKeySet.size
        && [...storedKeySet].every(k => freshKeySet.has(k));

      const storedByKey = new Map(level.columns.map(c => [c.key.toLowerCase(), c]));
      const columns: ReportColumnConfig[] = keysMatch
        ? freshColumns.map(fresh => {
          const stored = storedByKey.get(fresh.key.toLowerCase());
          if (!stored) return fresh;
          // Referenz nur bei tatsächlicher Änderung ersetzen, damit eine unveränderte Liste
          // (bereits richtige Reihenfolge/perStep) den `isSameArray`-Vergleich unten besteht und
          // kein unnötiges `changed` auslöst.
          return stored.perStep === fresh.perStep ? stored : {...stored, perStep: fresh.perStep};
        })
        : freshColumns;

      const isSameArray = columns.every((c, i) => c === level.columns[i]) && columns.length === level.columns.length;
      if (isSameArray) return level;
      levelChanged = true;
      return {...level, columns};
    });
    if (!levelChanged) return config;
    changed = true;
    return {...config, levels};
  });

  return changed ? normalized : configs;
}

/** Das Set-level KI-Parametermodell, wie es aus dem `configJson` eines KI-Regelsets kommt. */
export interface RulesetAiConfig {
  systemPrompt: string;
  templatePrompt: string;
  model: string;
  inputParameters: any[];
  checkParameters: any[];
  outputParameters: any[];
  tags: any[];
  contextFiles: any[];
}

/**
 * Liest das `configJson` eines KI-Regelsets in das Builder-Parametermodell.
 *
 * Einzige Stelle, die die `configJson`-Feldnamen kennt (`systemprompt`, `pruefprompt`, `kiModel`, …).
 * Sie werden an zwei Stellen gebraucht: beim Auswählen eines KI-Sets im Builder
 * (`builder-rule-step`) und beim Direktstart eines Sets (`RulesetService#prepareDirectRun`).
 * Liefe das auseinander, bekäme der Direktstart ein anderes Manifest als der handgebaute Check.
 *
 * Kaputtes/fehlendes JSON ergibt leere Werte – nicht als stiller Fallback, sondern weil das Backend
 * die Prompts zur Laufzeit ohnehin aus dem Set auflöst, wenn das Manifest sie nicht mitbringt.
 */
export function parseRulesetAiConfig(configJson: string | undefined | null): RulesetAiConfig {
  let cfg: any = {};
  try {
    cfg = configJson ? JSON.parse(configJson) : {};
  } catch {
    cfg = {};
  }
  return {
    systemPrompt: cfg?.systemprompt ?? '',
    templatePrompt: cfg?.pruefprompt ?? '',
    model: cfg?.kiModel ?? '',
    inputParameters: Array.isArray(cfg?.inputParameters) ? cfg.inputParameters : [],
    checkParameters: Array.isArray(cfg?.checkParameters) ? cfg.checkParameters : [],
    outputParameters: Array.isArray(cfg?.outputParameters) ? cfg.outputParameters : [],
    tags: Array.isArray(cfg?.tags) ? cfg.tags : [],
    contextFiles: Array.isArray(cfg?.contextFiles) ? cfg.contextFiles : [],
  };
}

/** Default-Berichtsperspektive eines Regelsets (Hybrid ist dateizentriert, sonst regelzentriert). */
export function defaultPerspectiveForRulesetType(type: RulesetType): ReportPerspective {
  return type === 'HYBRID' ? 'files_first' : 'rules_first';
}

/**
 * Der Prüfmodus, wie er sich im Lauf **tatsächlich** auswirkt.
 *
 * „Alle Dateien zusammen prüfen" ist bislang nur für KI-Regelsets umgesetzt: der hybride Pfad
 * startet je Element eine eigene Child-Instanz und prüft weiterhin jede Datei einzeln, IDS ohnehin.
 * Die Berichtsstruktur muss dem folgen – ohne diese Klammer verlöre ein hybrides Set mit
 * `COMBINED` seine Dateiebene, obwohl es echte Ergebnisse je Datei liefert.
 */
export function effectiveCheckMode(type: RulesetType, checkMode: CheckMode | undefined): CheckMode {
  return type === 'KI' && checkMode === 'COMBINED' ? 'COMBINED' : 'SINGLE';
}

function defaultLevelOrderKeys(checkMode: CheckMode | undefined, hasStructure: boolean): string[] {
  // Im gemeinsamen Prüflauf gibt es kein Ergebnis je Datei – die Dateiebene entfällt ganz,
  // siehe validLevelOrderings.
  if (checkMode === 'COMBINED') return hasStructure ? ['rules', 'structure'] : ['rules'];
  return hasStructure ? ['files', 'structure', 'rules'] : ['files', 'rules'];
}

/**
 * Die Default-Berichtskonfiguration eines Regelsets: Perspektive aus dem Typ, Ebenen-Reihenfolge aus
 * Prüfmodus und Prüfebene, Detailspalten bei KI aus den Ausgabeparametern.
 */
export function buildDefaultReportConfigForRuleset(
  rulesetId: string,
  rulesetType: RulesetType,
  pruefebene: string | undefined | null,
  outputParameters?: any[],
  checkMode?: CheckMode,
): RulesetReportConfig {
  if (rulesetType === 'IDS') {
    // IDS hat eine feste Datei→Spezifikation→Anforderungen-Struktur, unabhängig von der Prüfebene.
    return createDefaultReportConfig(rulesetId, rulesetType, defaultPerspectiveForRulesetType(rulesetType));
  }
  const detailColumns = rulesetType === 'KI' ? outputParamsToDetailColumns(outputParameters) : undefined;
  const orderKeys = defaultLevelOrderKeys(
    effectiveCheckMode(rulesetType, checkMode), hasStructureLevel(pruefebene),
  );
  return {
    rulesetId,
    rulesetType,
    perspective: defaultPerspectiveForRulesetType(rulesetType),
    levels: createReportLevelsFromOrder(orderKeys, undefined, detailColumns),
  };
}

/** Das Minimum, das der Direktstart über ein myCheck wissen muss. */
export interface DirectRunRuleset {
  id: string;
  name: string;
  description?: string;
  type: RulesetType;
  pruefebene?: string;
  checkMode?: CheckMode;
  /**
   * Die vom myCheck erlaubten Dateitypen. Schränkt die Dateiauswahl des Laufs ein – ohne das würde
   * z.B. ein IDS-myCheck PDFs zur Auswahl anbieten und der Lauf erst im Modul scheitern.
   */
  allowedFileTypes?: string[] | null;
  /**
   * Die vom myCheck erlaubten Dokumentenklassen („Dateiklassen") als IDs bzw. – bei global geteilten
   * Kopien – als `seed:<KEY>`-Tokens. Leer heißt „alle Klassen erlaubt".
   *
   * <p>Muss mitreisen, sonst bleibt eine Änderung der Klassen im Editor für den Lauf unsichtbar: der
   * Direktstart baut sein Manifest komplett aus diesem Objekt, und die Ausführung liest die Klassen
   * ausschließlich aus dem `file_selection`-Step des Manifests.
   */
  allowedDocumentClasses?: string[] | null;
}

/**
 * Die für ein myCheck zulässigen Dateitypen, normalisiert auf die Manifest-Tokens.
 *
 * Spiegelt `builder-file-step.getAllowedFileTypes()` für den Ein-myCheck-Fall: dort wird
 * `state.fileTypes` mit dieser Menge geschnitten und das Ergebnis ins Manifest geschrieben. Deklariert
 * ein myCheck keine (oder nur unbekannte) Typen, bleiben alle erlaubt – genau wie im Builder, wenn
 * nichts ausgewählt ist.
 */
function allowedFileTypesFor(ruleset: DirectRunRuleset): string[] {
  const allowed = new Set<string>();
  for (const type of ruleset.allowedFileTypes ?? []) {
    const normalized = normalizeFileTypeToken(type);
    if (normalized) allowed.add(normalized);
  }
  if (allowed.size === 0) return [...ALL_FILE_TYPES];
  const narrowed = ALL_FILE_TYPES.filter(type => allowed.has(type));
  return narrowed.length > 0 ? narrowed : [...ALL_FILE_TYPES];
}

/**
 * Startzustand eines aus einem myCheck angelegten Prüflaufs: wie ein leerer Prüflauf, nur die
 * Regelauswahl steht fest (Modus „Vordefiniert", das Set ausgewählt).
 *
 * <p>Bewusst NICHT {@link createDirectRunState} – der konfiguriert einen fertigen Lauf inklusive
 * Dateitypen und Berichtsstruktur. `ai` trägt die Set-Prompts, die der Builder beim manuellen
 * Auswählen ebenso nachlädt.
 */
export function createStateForRulesetSelection(
  ruleset: { id: string; type: RulesetType },
  name: string,
  ai?: RulesetAiConfig,
): MyCheckBuilderState {
  const state = createDefaultBuilderState();
  state.name = name;
  state.ruleSelectionMode = 'predefined';
  state.selectedRuleSetIds = [ruleset.id];
  state.selectedRulesetType = ruleset.type;
  state.selectedRulesetTypes = {[ruleset.id]: ruleset.type};

  if (ruleset.type === 'KI' && ai) {
    state.aiSystemPrompt = ai.systemPrompt;
    state.aiTemplatePrompt = ai.templatePrompt;
    state.aiModel = ai.model;
    state.aiInputParameters = ai.inputParameters;
    state.aiCheckParameters = ai.checkParameters;
    state.aiOutputParameters = ai.outputParameters;
    state.aiTags = ai.tags;
    state.aiContextFiles = ai.contextFiles;
  }

  return state;
}

/**
 * Builder-State für den **Direktstart** eines einzelnen Prüfregelsets: die Default-Prüflaufeinstellungen
 * plus dieses eine Set.
 *
 * Damit ist ein Regelset ohne vorher gebauten myCheck ausführbar. Das Ergebnis geht unverändert durch
 * {@link buildManifestFromState} – der Direktstart erzeugt also **exakt** das Manifest, das ein Nutzer
 * bekäme, der im Builder nur den Namen einträgt, das Set wählt und überall die Defaults bestätigt.
 * Deshalb bleibt der komplette Ausführungspfad (Regel-/Dateiauswahl, Bericht, Credits) unverändert.
 *
 * `fileSelectionMode` bleibt bewusst auf `'manual'` (dem Default): der Nutzer soll seine Dateien wie
 * gewohnt auswählen, statt still über alle Projektdateien zu laufen.
 *
 * `ruleSelectionMode` ist dagegen `'predefined'`: beim Direktstart steht genau ein Regelset fest, die
 * interaktive Regelauswahl wäre also eine Rückfrage ohne Entscheidung. Die Ausführung zeigt das Set
 * und seine Regeln read-only an (siehe `step-combined-select`), und die Engine löst den
 * `rule_selection`-Step intern aus den Settings auf.
 */
export function createDirectRunState(
  ruleset: DirectRunRuleset,
  ai?: RulesetAiConfig,
): MyCheckBuilderState {
  const state = createDefaultBuilderState();
  state.name = ruleset.name;
  state.description = ruleset.description ?? '';
  state.selectedRuleSetIds = [ruleset.id];
  state.selectedRulesetType = ruleset.type;
  state.selectedRulesetTypes = {[ruleset.id]: ruleset.type};
  // Beim Direktstart gibt es nur dieses eine Regelset – nichts auszuwählen (siehe Doc oben).
  state.ruleSelectionMode = 'predefined';
  // Dateiauswahl auf die Typen des myChecks einschränken (siehe allowedFileTypesFor).
  state.fileTypes = allowedFileTypesFor(ruleset);
  // Dokumentenklassen des myChecks („Dateiklassen") übernehmen. Leer bleibt leer und heißt
  // unverändert „alle Klassen erlaubt".
  state.documentClasses = (ruleset.allowedDocumentClasses ?? [])
    .map(documentClass => (documentClass ?? '').trim())
    .filter(Boolean);

  if (ruleset.type === 'KI' && ai) {
    state.aiSystemPrompt = ai.systemPrompt;
    state.aiTemplatePrompt = ai.templatePrompt;
    state.aiModel = ai.model;
    state.aiInputParameters = ai.inputParameters;
    state.aiCheckParameters = ai.checkParameters;
    state.aiOutputParameters = ai.outputParameters;
    state.aiTags = ai.tags;
    state.aiContextFiles = ai.contextFiles;
  }

  state.reportConfigs = [buildDefaultReportConfigForRuleset(
    ruleset.id, ruleset.type, ruleset.pruefebene, state.aiOutputParameters, ruleset.checkMode,
  )];
  return state;
}

/**
 * IDS-Berichte nutzen eine feste Datei→Spezifikation→Anforderungen-Struktur.
 * Die Spalten je Ebene sind konfigurierbar (Sichtbarkeit/Reihenfolge),
 * die Ebenen selbst aber fix.
 */
const IDS_DEFAULT_LEVELS: ReportLevelConfig[] = [
  {
    key: 'files',
    label: 'Dateien',
    columns: [
      {key: 'fileName', label: 'Dateiname', visible: true},
      {key: 'status', label: 'Status', visible: true},
    ],
  },
  {
    key: 'specifications',
    label: 'Spezifikationen',
    columns: [
      {key: 'status', label: 'Status', visible: true},
      {key: 'name', label: 'Spezifikation', visible: true},
      {key: 'applicability', label: 'Anwendbarkeit', visible: true},
      {key: 'elements', label: 'Bauteile', visible: true},
      {key: 'result', label: 'Ergebnis', visible: true},
    ],
  },
  {
    key: 'requirements',
    label: 'Anforderungen',
    columns: [
      {key: 'status', label: 'Status', visible: true},
      {key: 'facet', label: 'Facette', visible: true},
      {key: 'name', label: 'Name', visible: true},
      {key: 'value', label: 'Wert', visible: true},
      {key: 'result', label: 'Ergebnis', visible: true},
    ],
  },
];

function cloneLevels(levels: ReportLevelConfig[]): ReportLevelConfig[] {
  return levels.map(level => ({...level, columns: level.columns.map(col => ({...col}))}));
}

/** Erstellt die Default-Berichtskonfiguration für ein Prüfregelset. */
export function createDefaultReportConfig(
  rulesetId: string,
  rulesetType: RulesetType,
  perspective: ReportPerspective = 'rules_first',
  detailColumns?: ReportColumnConfig[],
): RulesetReportConfig {
  if (rulesetType === 'IDS') {
    return {
      rulesetId,
      rulesetType,
      perspective,
      levels: cloneLevels(IDS_DEFAULT_LEVELS),
    };
  }
  return {
    rulesetId,
    rulesetType,
    perspective,
    levels: createDefaultReportLevels(perspective, detailColumns),
  };
}

/**
 * Wendet eine neue Perspektive auf eine bestehende Konfiguration an.
 * Bereits vom Nutzer vorgenommene Spalten-Anpassungen (Reihenfolge/Sichtbarkeit)
 * bleiben je Ebene erhalten; nur die Ebenenreihenfolge wird neu bestimmt.
 */
export function applyPerspectiveToConfig(
  config: RulesetReportConfig,
  perspective: ReportPerspective,
): RulesetReportConfig {
  const existingByKey = new Map(config.levels.map(level => [level.key, level]));
  const levels = PERSPECTIVE_LEVEL_ORDER[perspective].map(level => {
    const existing = existingByKey.get(level.key);
    return existing
      ? {...existing, label: level.label}
      : {
        key: level.key,
        label: level.label,
        columns: (DEFAULT_LEVEL_COLUMNS[level.key] ?? []).map(col => ({...col})),
      };
  });
  return {...config, perspective, levels};
}

// ─── Manifest Mapping ─────────────────────────────────────────────────────────

const TRIGGER_MODULE_MAP: Record<TriggerMode, string> = {
  manual: 'manual_trigger',
  fileUpload: 'file_upload_trigger',
  schedule: 'schedule_trigger',
  webhook: 'webhook_trigger',
};

/** Interner Dispatcher im Backend: startet je ausgewähltem Regelset einen Sub-Run. */
export const MULTI_CHECK_EXECUTOR_MODULE = 'multi_check_executor';

/** Alle Regelset-Typen – für den typübergreifenden Dispatcher-Lauf. */
const ALL_RULESET_TYPES: RulesetType[] = ['IDS', 'KI', 'HYBRID'];

/**
 * Die an der Auswahl beteiligten Regelsets – vollständig gewählte plus solche, aus denen nur
 * einzelne Regeln stammen. Entspricht einer Gruppe je Eintrag (vgl. `rule_groups` im Backend)
 * und deckt sich mit den Berichts-Tabs (`builder-report-step.reportRulesets`).
 */
export function selectedRulesetIdsInSelection(state: MyCheckBuilderState): string[] {
  const ids: string[] = [];
  for (const id of [...(state.selectedRuleSetIds ?? []), ...(state.partiallySelectedRuleSetIds ?? [])]) {
    if (id && !ids.includes(id)) ids.push(id);
  }
  return ids;
}

/** Die in der Auswahl vorkommenden Regelset-Typen (leer, wenn nichts bekannt ist). */
export function rulesetTypesInSelection(state: MyCheckBuilderState): RulesetType[] {
  const types: RulesetType[] = [];
  for (const id of selectedRulesetIdsInSelection(state)) {
    const type = state.selectedRulesetTypes?.[id];
    if (type && !types.includes(type)) types.push(type);
  }
  return types;
}

/**
 * True, wenn der Lauf über den Dispatcher (`multi_check_executor`) laufen soll: sobald **mehr als
 * ein Regelset** beteiligt ist – unabhängig von den Typen.
 *
 * Bewusst nicht für Ein-Regelset-Checks: die bestehenden typspezifischen Ketten sind erprobt, und
 * ein unnötiger Sub-Run kostet nur Latenz.
 */
export function usesMultiCheckDispatcher(state: MyCheckBuilderState): boolean {
  return selectedRulesetIdsInSelection(state).length > 1;
}

/**
 * Build a Manifest from the builder state.
 *
 * Generates the same structure as the default ids-check.json:
 *   rule_selection_step → file_selection_step → check_data_preparation_step → check_execution_step
 *
 * For HYBRID rulesets, generates:
 *   rule_selection_step → file_selection_step → hybrid_ruleset_execution_step
 *
 * The execution engine determines step order via ${nodeId.outputs.*} dependency analysis.
 * Steps without variable refs are sorted alphabetically within their layer – so nodeIds
 * MUST sort in the intended execution order of the configured nodeIds.
 */
/**
 * Build the `ai_check` step settings carrying the KI prompts so they become part of the
 * workflow definition (manifest). Only non-empty values are emitted; when the ruleset is chosen
 * at runtime (manual mode) these are typically empty and the backend resolves them as a fallback.
 */
function buildAiCheckSettings(state: MyCheckBuilderState): { [key: string]: any } {
  const settings: { [key: string]: any } = {};
  if (state.aiSystemPrompt && state.aiSystemPrompt.trim()) {
    settings['system_prompt'] = state.aiSystemPrompt;
  }
  if (state.aiTemplatePrompt && state.aiTemplatePrompt.trim()) {
    settings['template_prompt'] = state.aiTemplatePrompt;
  }
  if (state.aiModel && state.aiModel.trim()) {
    settings['ai_model'] = state.aiModel;
  }
  // Full set-level parameter model so the manifest is self-describing (Variante B).
  if (state.aiInputParameters && state.aiInputParameters.length > 0) {
    settings['input_parameters'] = state.aiInputParameters;
  }
  if (state.aiCheckParameters && state.aiCheckParameters.length > 0) {
    settings['check_parameters'] = state.aiCheckParameters;
  }
  if (state.aiOutputParameters && state.aiOutputParameters.length > 0) {
    settings['output_parameters'] = state.aiOutputParameters;
  }
  if (state.aiTags && state.aiTags.length > 0) {
    settings['tags'] = state.aiTags;
  }
  if (state.aiContextFiles && state.aiContextFiles.length > 0) {
    settings['context_files'] = state.aiContextFiles;
  }
  return settings;
}

export function buildManifestFromState(state: MyCheckBuilderState, existing?: Manifest): Manifest {
  const steps: StepNode[] = [];
  const ruleSelectionNodeId = 'rule_selection_step';
  const fileSelectionNodeId = 'file_selection_step';
  const hybridExecutionNodeId = 'hybrid_ruleset_execution_step';
  const aiResolverNodeId = 'ai_rule_resolution_step';
  const aiCheckNodeId = 'ai_check_execution_step';
  const aiAggregationNodeId = 'ai_result_aggregation_step';
  const dataPreparationNodeId = 'check_data_preparation_step';
  const checkExecutionNodeId = 'check_execution_step';
  // Sortiert hinter 'file_selection_step' und 'rule_selection_step' – wichtig, weil Steps ohne
  // Variablenbezug innerhalb einer Ebene alphabetisch geordnet werden (siehe Doc-Kommentar oben).
  // Der Dispatcher referenziert beide Vorgänger, liegt also ohnehin in der nächsten Ebene.
  const multiCheckNodeId = 'multi_check_execution_step';

  // Mehrere Regelsets → Dispatcher-Kette; ein Regelset → erprobte typspezifische Kette.
  const isMulti = usesMultiCheckDispatcher(state);
  const isHybrid = !isMulti && state.selectedRulesetType === 'HYBRID';
  const isKi = !isMulti && state.selectedRulesetType === 'KI';

  // ── Step 1: Rule Selection ──────────────────────────────────────────────────
  // Auf den gewählten Regelset-Typ einschränken (auch IDS) – sonst zeigt
  // filterRulesetsByPlan() bei leerem ruleTypes-Array ALLE Regelsets (inkl. KI/HYBRID),
  // was zu einer typ-inkompatiblen Regelauswahl und einem abgebrochenen Check führt.
  // Beim Dispatcher-Lauf ist die Einschränkung falsch: dort sind mehrere Typen gewollt, und ein
  // Filter auf den (dann bedeutungslosen) Einzeltyp würde Regelsets bei der Ausführung verstecken.
  const ruleTypeFilter = isMulti
    ? [...ALL_RULESET_TYPES]
    : [state.selectedRulesetType];

  if (state.ruleSelectionMode === 'manual') {
    steps.push({
      nodeId: ruleSelectionNodeId,
      moduleId: 'rule_selection',
      inputs: {},
      settings: {
        selectionMode: 'manual',
        ruleTypes: ruleTypeFilter,
        selectedRuleSetIds: state.selectedRuleSetIds,
        selectedRuleIds: state.selectedRuleIds,
        partiallySelectedRuleSetIds: state.partiallySelectedRuleSetIds,
        selectedRulesetType: state.selectedRulesetType,
        // Typ je Regelset: hält eine gemischte Auswahl über Speichern/Laden hinweg darstellbar
        // (selectedRulesetType trägt nur einen Wert).
        selectedRulesetTypes: state.selectedRulesetTypes ?? {},
        showRuleFiltersAtExecution: state.showRuleFiltersAtExecution,
      },
      interactionRequired: {
        type: 'rule_select',
        config: {
          ruleTypes: ruleTypeFilter,
          selectedRuleSetIds: state.selectedRuleSetIds,
          selectedRuleIds: state.selectedRuleIds,
        },
      },
    });
  } else {
    steps.push({
      nodeId: ruleSelectionNodeId,
      moduleId: 'rule_selection',
      inputs: {},
      settings: {
        selectionMode: 'predefined',
        ruleTypes: ruleTypeFilter,
        selectedRuleSetIds: state.selectedRuleSetIds,
        selectedRuleIds: state.selectedRuleIds,
        partiallySelectedRuleSetIds: state.partiallySelectedRuleSetIds,
        selectedRulesetType: state.selectedRulesetType,
        // Typ je Regelset: hält eine gemischte Auswahl über Speichern/Laden hinweg darstellbar
        // (selectedRulesetType trägt nur einen Wert).
        selectedRulesetTypes: state.selectedRulesetTypes ?? {},
        showRuleFiltersAtExecution: state.showRuleFiltersAtExecution,
      },
    });
  }

  // ── Step 2: File Selection ──────────────────────────────────────────────────
  if (state.fileSelectionMode === 'manual') {
    steps.push({
      nodeId: fileSelectionNodeId,
      moduleId: 'file_selection',
      inputs: {},
      settings: {
        selectionMode: 'manual',
        // Keine ['IFC']-Vorbelegung: eine leere Auswahl bleibt leer und gilt als unvollständig
        // (deckt sich mit der Pflichtfeld-Validierung und der Backend-Vollständigkeitsprüfung).
        fileTypes: state.fileTypes,
        documentClasses: state.documentClasses,
        minFiles: state.minFiles,
        maxFiles: state.maxFiles,
        userHint: state.userHint,
      },
      interactionRequired: {
        type: 'file_select',
        config: {
          fileTypes: state.fileTypes,
          documentClasses: state.documentClasses,
          minFiles: state.minFiles,
          maxFiles: state.maxFiles,
          userHint: state.userHint,
        },
      },
    });
  } else {
    // Bei Scope 'all' explizit alle bekannten Dateitypen eintragen,
    // bei 'selected' die konfigurierten Typen verwenden, Fallback auf IFC.
    const predefinedFileTypes = state.predefinedFileSelectionScope === 'all'
      ? ALL_FILE_TYPES
      : state.fileTypes;
    const predefinedDocClasses = state.predefinedFileSelectionScope === 'all'
      ? []
      : state.documentClasses;
    steps.push({
      nodeId: fileSelectionNodeId,
      moduleId: 'file_selection',
      inputs: {},
      settings: {
        selectionMode: 'predefined',
        predefinedFileSelectionScope: state.predefinedFileSelectionScope,
        predefinedUploadPeriod: state.predefinedUploadPeriod,
        predefinedCreatorIds: state.predefinedCreatorIds,
        predefinedFileNameRegex: state.predefinedFileNameRegex,
        fileTypes: predefinedFileTypes,
        documentClasses: predefinedDocClasses,
        minFiles: state.minFiles,
        maxFiles: state.maxFiles,
        userHint: state.userHint,
      },
    });
  }

  // Report configuration metadata stored in manifest and passed to workflow modules
  // so runtime/export paths can apply the same settings as the frontend.
  const reportConfig = {
    inAppReport: state.inAppReport,
    excelExport: state.excelExport,
    pdfExport: state.pdfExport,
    bcfExport: state.bcfExport,
    createTask: state.createTask,
    taskCreationLevel: state.taskCreationLevel,
    taskNamePrefix: state.taskNamePrefix,
    storeInProjectFolder: state.storeInProjectFolder,
    reportTargetFolder: state.reportTargetFolder,
    reportFileNamePattern: state.reportFileNamePattern,
    emailNotification: state.emailNotification,
    notificationMemberIds: state.notificationMemberIds,
    recipients: state.recipients,
    // Berichtsstruktur je Prüfregelset (Perspektive + Ebenen + Spalten)
    reportConfigs: state.reportConfigs,
  };

  // ── Step 3+4: Execution path depends on ruleset type ────────────────────────
  if (isMulti) {
    // Mehrere Regelsets: ein interner Dispatcher startet pro Regelset einen Sub-Run mit der
    // typrichtigen, bereits existierenden Kette (seeded 'ids-check' / 'ai-check') und sammelt
    // die Teilberichte in outputData.reports[]. Ersetzt die typspezifische Kette komplett.
    const multiInputs: { [key: string]: any } = {
      // Die Gruppierung nach Regelset leitet der interne rule_selection-Step im Backend ab
      // (bzw. bei manueller Auswahl schickt sie das Frontend beim Fortsetzen direkt mit).
      rule_groups: `\${${ruleSelectionNodeId}.outputs.rule_groups}`,
      project_files: `\${${fileSelectionNodeId}.outputs.project_files}`,
    };

    if (state.fileSelectionMode === 'manual') {
      multiInputs['file_type'] = `\${${fileSelectionNodeId}.outputs.file_type}`;
    } else if (state.predefinedFileSelectionScope === 'all') {
      multiInputs['file_type'] = ALL_FILE_TYPES;
    } else {
      multiInputs['file_type'] = state.fileTypes[0] ?? 'IFC';
    }

    steps.push({
      nodeId: multiCheckNodeId,
      moduleId: MULTI_CHECK_EXECUTOR_MODULE,
      inputs: multiInputs,
    });
  } else if (isHybrid) {
    // HYBRID path: single executor step that dynamically runs all rules in the ruleset
    const hybridInputs: { [key: string]: any } = {};

    if (state.ruleSelectionMode === 'manual') {
      hybridInputs['ruleset_id'] = '${rule_selection_step.outputs.ruleset_id}';
    } else if (state.selectedRuleSetIds.length > 0) {
      hybridInputs['ruleset_id'] = state.selectedRuleSetIds[0];
    }

    hybridInputs['project_files'] = '${file_selection_step.outputs.project_files}';

    steps.push({
      nodeId: hybridExecutionNodeId,
      moduleId: 'HYBRID_RULESET_EXECUTOR',
      inputs: hybridInputs,
    });
  } else if (isKi) {
    // KI path: promptbasierte Prüfung über die ai/**-Modulkette (ohne Klassifizierung)
    //   ai_rule_resolver → ai_check (Fan-out) → ai_result_aggregator
    // Die ausgewählten Projektdateien werden direkt (ohne HOAI-Klassifizierung) als
    // `classified_files` an den Resolver übergeben; Regeln ohne Klassifikation matchen alle Dateien.
    const rulesetIdRef = state.ruleSelectionMode === 'manual'
      ? '${rule_selection_step.outputs.ruleset_id}'
      : (state.selectedRuleSetIds.length > 0 ? state.selectedRuleSetIds[0] : undefined);
    const rulesetTypeRef = state.ruleSelectionMode === 'manual'
      ? '${rule_selection_step.outputs.ruleset_type}'
      : 'KI';

    const resolverInputs: { [key: string]: any } = {
      classified_files: '${file_selection_step.outputs.project_files}',
      ruleset_type: rulesetTypeRef,
    };
    if (rulesetIdRef !== undefined) {
      resolverInputs['ruleset_id'] = rulesetIdRef;
    }
    steps.push({
      nodeId: aiResolverNodeId,
      moduleId: 'ai_rule_resolver',
      inputs: resolverInputs,
    });

    steps.push({
      nodeId: aiCheckNodeId,
      moduleId: 'ai_check',
      inputs: {
        file_rule_pairs: '${ai_rule_resolution_step.outputs.file_rule_pairs}',
      },
      settings: buildAiCheckSettings(state),
    });

    steps.push({
      nodeId: aiAggregationNodeId,
      moduleId: 'ai_result_aggregator',
      inputs: {
        check_results: '${ai_check_execution_step.outputs.check_results}',
        report_config: reportConfig,
        selected_ruleset_ids: state.selectedRuleSetIds,
        selected_rule_ids: state.selectedRuleIds,
        selected_ruleset_type: state.selectedRulesetType,
      },
    });
  } else {
    // Standard path: data preparation + check execution
    const dataPrepInputs: { [key: string]: any } = {};

    if (state.ruleSelectionMode === 'manual') {
      dataPrepInputs['rule_id'] = '${rule_selection_step.outputs.rule_id}';
      dataPrepInputs['rule_type'] = '${rule_selection_step.outputs.rule_type}';
      dataPrepInputs['check_type'] = '${rule_selection_step.outputs.ruleset_type}';
      // Teilauswahl einzelner IDS-Spezifikationen innerhalb eines Sets: dafür gibt es keine
      // auflösbare rule_id (siehe step-combined-select.component.ts), stattdessen schickt die
      // Regelauswahl direkt die clientseitig gefilterte IDS-XML mit. rule_content (aus einer
      // erfolgreich aufgelösten rule_id) hat in buildCheckDataPreparationOutputs immer Vorrang,
      // daher ist diese Zuordnung für andere Regeltypen/Auswahlarten unschädlich.
      dataPrepInputs['ids_xml'] = '${rule_selection_step.outputs.ids_xml}';
    } else if (state.selectedRuleIds.length > 0) {
      dataPrepInputs['rule_id'] = state.selectedRuleIds[0];
      dataPrepInputs['rule_type'] = 'IDS';
      dataPrepInputs['check_type'] = 'IDS';
    } else if (state.selectedRuleSetIds.length > 0) {
      // IDS-Regeln werden im Builder als Rulesets (selectedRuleSetIds) gespeichert,
      // da jede IDS-Regel ihr eigenes Ruleset ist. ruleset_id == rule_id für IDS.
      dataPrepInputs['rule_id'] = state.selectedRuleSetIds[0];
      dataPrepInputs['rule_type'] = 'IDS';
      dataPrepInputs['check_type'] = 'IDS';
    }

    // file_selection returns project_files in both modes; check_data_preparation derives file_uri from that list.
    dataPrepInputs['project_files'] = '${file_selection_step.outputs.project_files}';
    if (state.fileSelectionMode === 'manual') {
      dataPrepInputs['file_type'] = '${file_selection_step.outputs.file_type}';
    } else if (state.predefinedFileSelectionScope === 'all') {
      // Scope 'all': alle Dateitypen erlaubt → alle Typen übergeben
      dataPrepInputs['file_type'] = ALL_FILE_TYPES;
    } else {
      dataPrepInputs['file_type'] = state.fileTypes[0] ?? 'IFC';
    }

    steps.push({
      nodeId: dataPreparationNodeId,
      moduleId: 'check_data_preparation',
      inputs: dataPrepInputs,
    });

    steps.push({
      nodeId: checkExecutionNodeId,
      moduleId: 'check_execution',
      inputs: {
        file_uri: '${check_data_preparation_step.outputs.file_uri}',
        rule_content: '${check_data_preparation_step.outputs.rule_content}',
        check_type: '${check_data_preparation_step.outputs.check_type}',
      },
    });
  }

  // Link steps via onSuccess
  for (let i = 0; i < steps.length - 1; i++) {
    steps[i].onSuccess = steps[i + 1].nodeId;
  }

  return {
    name: state.name,
    version: existing?.version ?? 'v1',
    description: state.description,
    // Vorbelegung; wird beim Speichern serverseitig neu berechnet und ggf. überschrieben.
    isValid: isStateValid(state),
    trigger: {
      nodeId: 'trigger_0',
      moduleId: TRIGGER_MODULE_MAP[state.triggerMode] ?? 'manual_trigger',
      config: {reportConfig},
      onSuccess: steps.length > 0 ? steps[0].nodeId : undefined,
    },
    steps,
  };
}

/** Parse an existing Manifest back into a builder state. */
export function parseManifestToState(manifest: Manifest): Partial<MyCheckBuilderState> {
  const partial: Partial<MyCheckBuilderState> = {
    name: manifest.name ?? '',
    description: manifest.description ?? '',
  };

  // Trigger
  const triggerModuleId = manifest.trigger?.moduleId ?? 'manual_trigger';
  const triggerEntry = Object.entries(TRIGGER_MODULE_MAP).find(([, v]) => v === triggerModuleId);
  partial.triggerMode = (triggerEntry?.[0] as TriggerMode) ?? 'manual';

  // Report config from trigger metadata
  const reportConfig = manifest.trigger?.config?.['reportConfig'];
  if (reportConfig) {
    partial.inAppReport = reportConfig['inAppReport'] ?? true;
    partial.excelExport = reportConfig['excelExport'] ?? false;
    partial.pdfExport = reportConfig['pdfExport'] ?? false;
    partial.bcfExport = reportConfig['bcfExport'] ?? false;
    partial.createTask = reportConfig['createTask'] ?? false;
    partial.taskCreationLevel = reportConfig['taskCreationLevel'] ?? 'per_check';
    partial.taskNamePrefix = reportConfig['taskNamePrefix'] ?? '';
    partial.storeInProjectFolder = reportConfig['storeInProjectFolder'] ?? false;
    partial.reportTargetFolder = reportConfig['reportTargetFolder'] ?? '';
    partial.reportFileNamePattern = reportConfig['reportFileNamePattern'] ?? '';
    partial.emailNotification = reportConfig['emailNotification'] ?? false;
    partial.notificationMemberIds = reportConfig['notificationMemberIds'] ?? [];
    partial.recipients = reportConfig['recipients'] ?? [];
    const parsedReportConfigs = Array.isArray(reportConfig['reportConfigs'])
      ? reportConfig['reportConfigs'] as RulesetReportConfig[]
      : [];
    partial.reportConfigs = normalizeIdsReportConfigLabels(parsedReportConfigs);
  }

  // Parse steps
  for (const step of manifest.steps ?? []) {
    if (step.moduleId === 'rule_selection' || step.moduleId === 'check_selector') {
      const settings = step.settings ?? {};
      if (settings.selectionMode === 'predefined') {
        partial.ruleSelectionMode = 'predefined';
        partial.selectedRuleSetIds = settings['selectedRuleSetIds'] ?? [];
        partial.selectedRuleIds = settings['selectedRuleIds'] ?? [];
        partial.partiallySelectedRuleSetIds = settings['partiallySelectedRuleSetIds'] ?? [];
        partial.selectedRulesetTypes = settings['selectedRulesetTypes'] ?? {};
        partial.selectedRulesetType = settings['selectedRulesetType'] ?? 'IDS';
        partial.showRuleFiltersAtExecution = settings['showRuleFiltersAtExecution'] ?? false;
      } else if (step.interactionRequired) {
        partial.ruleSelectionMode = 'manual';
        partial.selectedRuleSetIds = settings['selectedRuleSetIds'] ?? step.interactionRequired?.config?.['selectedRuleSetIds'] ?? [];
        partial.selectedRuleIds = settings['selectedRuleIds'] ?? step.interactionRequired?.config?.['selectedRuleIds'] ?? [];
        partial.partiallySelectedRuleSetIds = settings['partiallySelectedRuleSetIds'] ?? [];
        partial.selectedRulesetTypes = settings['selectedRulesetTypes'] ?? {};
        partial.showRuleFiltersAtExecution = settings['showRuleFiltersAtExecution'] ?? false;
        // Der Builder schreibt selectedRulesetType immer mit – bevorzugen. Die Ableitung aus
        // ruleTypes ist nur der Fallback für Alt-Manifeste und wäre bei der Dispatcher-Kette
        // sogar falsch: dort stehen bewusst ALLE Typen in ruleTypes (typübergreifende Auswahl),
        // die Ableitung würde daraus fälschlich 'HYBRID' machen.
        const ruleTypes = settings['ruleTypes'] ?? step.interactionRequired?.config?.['ruleTypes'] ?? [];
        const storedType = settings['selectedRulesetType'];
        if (storedType) {
          partial.selectedRulesetType = storedType;
        } else if (ruleTypes.length < ALL_RULESET_TYPES.length) {
          if (ruleTypes.includes('HYBRID')) {
            partial.selectedRulesetType = 'HYBRID';
          } else if (ruleTypes.includes('KI')) {
            partial.selectedRulesetType = 'KI';
          }
        }
      } else {
        partial.ruleSelectionMode = step.inputs?.['ruleSelectionMode'] ?? 'predefined';
        partial.selectedRuleSetIds = step.inputs?.['ruleSetIds'] ?? settings['selectedRuleSetIds'] ?? [];
        partial.selectedRuleIds = step.inputs?.['ruleIds'] ?? [];
        partial.partiallySelectedRuleSetIds = settings['partiallySelectedRuleSetIds'] ?? [];
        partial.showRuleFiltersAtExecution = step.inputs?.['showRuleFiltersAtExecution'] ?? settings['showRuleFiltersAtExecution'] ?? false;
      }
    } else if (step.moduleId === 'file_selection' || step.moduleId === 'file_selector') {
      const settings = step.settings ?? {};
      if (settings.selectionMode) {
        partial.fileSelectionMode = settings.selectionMode as SelectionMode;
        partial.predefinedFileSelectionScope = settings['predefinedFileSelectionScope'] ?? 'all';
        partial.predefinedUploadPeriod = settings['predefinedUploadPeriod'] ?? 'all_time';
        partial.predefinedCreatorIds = settings['predefinedCreatorIds'] ?? [];
        partial.predefinedFileNameRegex = settings['predefinedFileNameRegex'] ?? '';
        partial.fileTypes = settings['fileTypes'] ?? [];
        partial.documentClasses = settings['documentClasses'] ?? [];
        partial.minFiles = settings['minFiles'] ?? 1;
        // Fehlender Wert bedeutet unbegrenzt – kein Fallback auf 1 (anders als bei minFiles).
        partial.maxFiles = settings['maxFiles'] ?? undefined;
        partial.userHint = settings['userHint'] ?? '';
      } else if (step.interactionRequired) {
        partial.fileSelectionMode = 'manual';
        partial.predefinedFileSelectionScope = 'all';
        partial.predefinedUploadPeriod = 'all_time';
        partial.predefinedCreatorIds = [];
        partial.predefinedFileNameRegex = '';
        const config = step.interactionRequired.config ?? {};
        partial.fileTypes = config['fileTypes'] ?? [];
        partial.documentClasses = config['documentClasses'] ?? [];
        partial.minFiles = config['minFiles'] ?? 1;
        partial.maxFiles = config['maxFiles'] ?? undefined;
        partial.userHint = config['userHint'] ?? '';
      } else {
        partial.fileSelectionMode = step.inputs?.['fileSelectionMode'] ?? 'predefined';
        partial.predefinedFileSelectionScope = step.inputs?.['predefinedFileSelectionScope'] ?? 'all';
        partial.predefinedUploadPeriod = step.inputs?.['predefinedUploadPeriod'] ?? 'all_time';
        partial.predefinedCreatorIds = step.inputs?.['predefinedCreatorIds'] ?? [];
        partial.predefinedFileNameRegex = step.inputs?.['predefinedFileNameRegex'] ?? '';
        partial.fileTypes = step.inputs?.['fileTypes'] ?? [];
        partial.documentClasses = step.inputs?.['documentClasses'] ?? [];
        partial.minFiles = step.inputs?.['minFiles'] ?? 1;
        partial.maxFiles = step.inputs?.['maxFiles'] ?? undefined;
        partial.userHint = step.inputs?.['userHint'] ?? '';
      }
    } else if (step.moduleId === 'check_data_preparation') {
      // Extract predefined rule/file from static inputs (non-variable refs).
      // NUR als Fallback für Altmanifeste ohne verwertbare rule_selection-Settings: dieser Step
      // kommt nach dem rule_selection-Step, würde dessen (korrekte) Auswahl also überschreiben.
      // Vor allem darf dieselbe ID nicht gleichzeitig in selectedRuleSetIds UND selectedRuleIds
      // landen – sonst zählt die Zusammenfassung sie doppelt ("1 Set + 1 einzelne Regel") und die
      // Regelauswahl im Builder zeigt eine Auswahl an, die es so nie gab.
      const ruleId = step.inputs?.['rule_id'];
      const hasParsedSelection = (partial.selectedRuleSetIds?.length ?? 0) > 0
        || (partial.selectedRuleIds?.length ?? 0) > 0;
      if (typeof ruleId === 'string' && ruleId && !ruleId.startsWith('${') && !hasParsedSelection) {
        partial.ruleSelectionMode = 'predefined';
        // IDS-Regeln werden als Regelsets geführt (ruleset_id == rule_id), daher nur dort eintragen.
        partial.selectedRuleSetIds = [ruleId];
        partial.selectedRuleIds = [];
      }
    } else if (step.moduleId === MULTI_CHECK_EXECUTOR_MODULE) {
      // Dispatcher-Kette (mehrere Regelsets): Die vollständige Auswahl steht bereits in den
      // rule_selection-Settings und wurde oben gelesen. Hier absichtlich NICHTS überschreiben –
      // die typspezifischen Zweige unten setzen selectedRuleSetIds auf [rulesetId] und würden
      // eine Mehrfachauswahl beim Neuladen des Builders auf ein Regelset zusammenschrumpfen.
      // (Genau dieser Effekt war einer der Gründe, warum mehrere Regelsets bisher nicht hielten.)
    } else if (step.moduleId === 'HYBRID_RULESET_EXECUTOR') {
      // Hybrid execution step — mark ruleset type as HYBRID
      partial.selectedRulesetType = 'HYBRID';
      const rulesetId = step.inputs?.['ruleset_id'];
      if (rulesetId && !rulesetId.startsWith('${')) {
        partial.ruleSelectionMode = 'predefined';
        partial.selectedRuleSetIds = [rulesetId];
      }
    } else if (step.moduleId === 'hoai_rule_resolver' || step.moduleId === 'ai_rule_resolver') {
      // KI (promptbasierte Prüfung) execution path — mark ruleset type as KI
      partial.selectedRulesetType = 'KI';
      const rulesetId = step.inputs?.['ruleset_id'];
      if (rulesetId && !rulesetId.startsWith('${')) {
        partial.ruleSelectionMode = 'predefined';
        partial.selectedRuleSetIds = [rulesetId];
      }
    } else if (step.moduleId === 'hoai_check' || step.moduleId === 'ai_check') {
      // AI execution: recover prompts baked into the definition (ai_check settings)
      const settings = step.settings ?? {};
      if (settings['system_prompt'] != null) partial.aiSystemPrompt = settings['system_prompt'];
      if (settings['template_prompt'] != null) partial.aiTemplatePrompt = settings['template_prompt'];
      const modelValue = settings['ai_model'] ?? settings['ki_model'];
      if (modelValue != null) partial.aiModel = modelValue;
      if (Array.isArray(settings['input_parameters'])) partial.aiInputParameters = settings['input_parameters'];
      if (Array.isArray(settings['check_parameters'])) partial.aiCheckParameters = settings['check_parameters'];
      if (Array.isArray(settings['output_parameters'])) partial.aiOutputParameters = settings['output_parameters'];
      if (Array.isArray(settings['tags'])) partial.aiTags = settings['tags'];
      if (Array.isArray(settings['context_files'])) partial.aiContextFiles = settings['context_files'];
      // Legacy format: report as step
      partial.inAppReport = step.inputs?.['inAppReport'] ?? true;
      partial.excelExport = step.inputs?.['excelExport'] ?? false;
      partial.createTask = step.inputs?.['createTask'] ?? false;
      partial.storeInProjectFolder = step.inputs?.['storeInProjectFolder'] ?? false;
      partial.recipients = step.inputs?.['recipients'] ?? [];
    }
  }

  return partial;
}

// ─── Vollständigkeit / Pflichtfeld-Validierung ─────────────────────────────────

/** Eine fehlende Pflichtangabe, dem Builder-Schritt zugeordnet. */
export interface MissingRequiredField {
  /** Builder-Schritt-Index (0=Start, 1=Regelauswahl, 2=Dateiauswahl, 3=Bericht). */
  step: number;
  /** Stabiler Feldschlüssel (für gezielte Inline-Hervorhebung). */
  field: string;
  /** Nutzerlesbare Meldung. */
  message: string;
}

/**
 * Ermittelt alle fehlenden Pflichtangaben (im UI mit „*" markiert), gruppiert nach Builder-Schritt.
 *
 * WICHTIG: Diese Regeln MÜSSEN mit der serverseitigen Vollständigkeitsprüfung
 * (`WorkflowManifestParser.findCompletenessIssues`) übereinstimmen, da das Backend `isValid`
 * unabhängig neu berechnet und einen abweichenden Frontend-Wert überschreibt.
 */
export function getMissingRequiredFields(state: MyCheckBuilderState): MissingRequiredField[] {
  const missing: MissingRequiredField[] = [];

  // Schritt 0 – Start: Name
  if (!state.name.trim()) {
    missing.push({step: 0, field: 'name', message: 'Name ist ein Pflichtfeld.'});
  }

  // Schritt 1 – Regelauswahl: mind. ein Set oder eine Regel (unabhängig vom Modus)
  if (state.selectedRuleSetIds.length === 0 && state.selectedRuleIds.length === 0) {
    missing.push({
      step: 1,
      field: 'rules',
      message: 'Mindestens ein myCheck oder eine Prüfregel muss ausgewählt sein.',
    });
  }

  // Schritt 2 – Dateiauswahl: Dateitypen je Modus/Scope + sinnvolle Mengen
  const fileTypesEmpty = state.fileTypes.length === 0;
  if (state.fileSelectionMode === 'manual' && fileTypesEmpty) {
    missing.push({step: 2, field: 'fileTypes', message: 'Mindestens ein Dateityp muss gewählt sein.'});
  } else if (state.fileSelectionMode === 'predefined'
    && state.predefinedFileSelectionScope === 'selected'
    && fileTypesEmpty) {
    missing.push({
      step: 2,
      field: 'fileTypes',
      message: 'Bei vordefinierter Dateiauswahl muss mindestens ein Dateityp gewählt sein.',
    });
  }
  if (state.minFiles < 1) {
    missing.push({step: 2, field: 'minFiles', message: 'Min. Dateien muss mindestens 1 sein.'});
    // Kein maxFiles (unbegrenzt) kann nie kleiner als minFiles sein.
  } else if (state.maxFiles != null && state.minFiles > state.maxFiles) {
    missing.push({step: 2, field: 'maxFiles', message: 'Min. Dateien darf nicht größer als Max. Dateien sein.'});
  }

  return missing;
}

/** True, wenn alle Pflichtangaben gesetzt sind (Check ist vollständig/valide). */
export function isStateValid(state: MyCheckBuilderState): boolean {
  return getMissingRequiredFields(state).length === 0;
}

/** Builder-Schritte (0–3), denen mindestens eine fehlende Pflichtangabe zugeordnet ist. */
export function getInvalidSteps(state: MyCheckBuilderState): number[] {
  return [...new Set(getMissingRequiredFields(state).map(m => m.step))].sort((a, b) => a - b);
}

/** Validate builder state, returning a list of error messages. */
export function validateBuilderState(state: MyCheckBuilderState): string[] {
  return getMissingRequiredFields(state).map(m => m.message);
}
